import {
  JOURNEY_ABANDONMENT_REASON_VALUES,
  JOURNEY_OPTIONAL_FIELDS,
  JOURNEY_RECOVERY_OUTCOME_VALUES,
  JOURNEY_REQUIRED_FIELDS,
  JOURNEY_STEP_STATUS_VALUES,
} from './constants';
import { mapApiErrorToTelemetry } from './failureSchema';
import { buildRuntimeContext, normalizeLifecycleEnv } from './envContext';
import { resolveRegionFromHost } from './domainMapping';
import { JourneyEventContext, JourneyLifecycleEvent, JourneyStepStatus } from './types';
import { getJourneyPerformanceContext } from './performanceContext';

export interface JourneyStepInput {
  journey_name: string;
  step_name: string;
  step_order: number;
  step_status: JourneyStepStatus;
  duration_ms?: number;
  journey_id?: string;
  env?: string;
  region?: string;
  operation?: string;
  http_status?: number;
  error_code?: string;
  retryable?: boolean;
  abandonment_reason?: string;
  recovery_outcome?: string;
  time_to_first_upload_ms?: number;
  time_to_preview_open_ms?: number;
  time_to_search_results_ms?: number;
  app_version?: string;
  build_id?: string;
  feature_flag?: string;
  user_role?: string;
  tenant_type?: string;
  entry_point?: string;
  route?: string;
}

type JourneyEmitter = (action: JourneyLifecycleEvent, payload: Record<string, unknown>) => void;

const startedAtMap = new Map<string, number>();
const terminalJourneyIds = new Set<string>();
const startedAtByStep = new Map<string, number>();

const w = window as any;

const safeLower = (value: string | undefined): string => (value || '').toLowerCase().trim();

const approvedOptionalFields = new Set<string>(JOURNEY_OPTIONAL_FIELDS);
const allowedStepStatuses = new Set<string>(JOURNEY_STEP_STATUS_VALUES);
const allowedAbandonmentReasons = new Set<string>(JOURNEY_ABANDONMENT_REASON_VALUES);
const allowedRecoveryOutcomes = new Set<string>(JOURNEY_RECOVERY_OUTCOME_VALUES);

const nonPiiPattern = /^[a-zA-Z0-9._\-:/ ]+$/;
const piiHintPattern = /(@|email|phone|username|ssn|token)/i;

const buildId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `j_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
};

const toStepKey = (journeyId: string, stepName: string, stepOrder: number): string => {
  return `${journeyId}:${stepOrder}:${stepName}`;
};

const toJourneyStorageKey = (journeyName: string): string => `rum_journey:${journeyName}`;

const readEnvVar = (name: string): string | undefined => {
  return process.env[name] || w?._env_?.[name];
};

const isApprovedDimension = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 64) return false;
  if (piiHintPattern.test(trimmed)) return false;
  return nonPiiPattern.test(trimmed);
};

const getRuntimeJourneyDefaults = () => {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const configuredEnv = readEnvVar('REACT_APP_KUBE_NAMESPACE') || readEnvVar('REACT_APP_BACKEND_ENV') || 'dev';
  const region = resolveRegionFromHost(host);
  const runtimeContext = buildRuntimeContext(normalizeLifecycleEnv(configuredEnv), host, region);
  return {
    env: runtimeContext.env,
    region: runtimeContext.region,
    app_version: readEnvVar('REACT_APP_VERSION'),
    build_id: readEnvVar('REACT_APP_BUILD_ID') || readEnvVar('REACT_APP_KUBE_DEPLOYMENT'),
    feature_flag: readEnvVar('REACT_APP_FEATURE_FLAG'),
  };
};

const withGovernance = (payload: Record<string, unknown>): Record<string, unknown> => {
  const output: Record<string, unknown> = {};

  Object.entries(payload).forEach(([key, value]) => {
    if (JOURNEY_REQUIRED_FIELDS.includes(key as any)) {
      output[key] = value;
      return;
    }

    if (!approvedOptionalFields.has(key)) {
      return;
    }

    if (key === 'abandonment_reason' && typeof value === 'string' && !allowedAbandonmentReasons.has(value)) {
      return;
    }

    if (key === 'recovery_outcome' && typeof value === 'string' && !allowedRecoveryOutcomes.has(value)) {
      return;
    }

    if (['feature_flag', 'user_role', 'tenant_type', 'entry_point', 'app_version', 'build_id'].includes(key)) {
      if (!isApprovedDimension(value)) return;
    }

    if (value !== undefined && value !== null && value !== '') {
      output[key] = value;
    }
  });

  return output;
};

export const createJourneyId = (): string => buildId();

export const getJourneyId = (journeyName: string): string | undefined => {
  try {
    return sessionStorage.getItem(toJourneyStorageKey(journeyName)) || undefined;
  } catch {
    return undefined;
  }
};

export const setJourneyId = (journeyName: string, journeyId: string): void => {
  try {
    sessionStorage.setItem(toJourneyStorageKey(journeyName), journeyId);
  } catch {
    // fail-open
  }
};

export const clearJourneyId = (journeyName: string): void => {
  try {
    sessionStorage.removeItem(toJourneyStorageKey(journeyName));
  } catch {
    // fail-open
  }
};

const normalizeJourneyPayload = (input: JourneyStepInput): JourneyEventContext | undefined => {
  if (!input.journey_name || !input.step_name || !Number.isInteger(input.step_order) || input.step_order <= 0) {
    return undefined;
  }

  const status = safeLower(input.step_status) as JourneyStepStatus;
  if (!allowedStepStatuses.has(status)) {
    return undefined;
  }

  const journey_id = input.journey_id || createJourneyId();
  const runtimeDefaults = getRuntimeJourneyDefaults();
  const env = normalizeLifecycleEnv(input.env || runtimeDefaults.env);
  const region = input.region || runtimeDefaults.region;
  const now = Date.now();
  const stepKey = toStepKey(journey_id, input.step_name, input.step_order);
  const knownStartedAt = startedAtByStep.get(stepKey);
  const duration = input.duration_ms ?? (knownStartedAt ? now - knownStartedAt : 0);

  return {
    journey_id,
    journey_name: input.journey_name,
    step_name: input.step_name,
    step_order: input.step_order,
    step_status: status,
    duration_ms: Math.max(0, Math.round(duration)),
    env,
    region: region as any,
    operation: input.operation,
    http_status: input.http_status,
    error_code: input.error_code,
    retryable: input.retryable,
    abandonment_reason: input.abandonment_reason as any,
    recovery_outcome: input.recovery_outcome as any,
    time_to_first_upload_ms: input.time_to_first_upload_ms,
    time_to_preview_open_ms: input.time_to_preview_open_ms,
    time_to_search_results_ms: input.time_to_search_results_ms,
    app_version: input.app_version || runtimeDefaults.app_version,
    build_id: input.build_id || runtimeDefaults.build_id,
    feature_flag: input.feature_flag || runtimeDefaults.feature_flag,
    user_role: input.user_role,
    tenant_type: input.tenant_type,
    entry_point: input.entry_point,
    route: input.route,
    ...getJourneyPerformanceContext(),
  };
};

const emit = (event: JourneyLifecycleEvent, input: JourneyStepInput, emitter: JourneyEmitter): string | undefined => {
  const payload = normalizeJourneyPayload(input);
  if (!payload) return undefined;
  const stepKey = toStepKey(payload.journey_id, payload.step_name, payload.step_order);

  if (payload.step_status === 'started') {
    startedAtByStep.set(stepKey, Date.now());
  } else {
    startedAtByStep.delete(stepKey);
  }

  if ((event === 'journey_complete' || event === 'journey_abandon') && terminalJourneyIds.has(payload.journey_id)) {
    return payload.journey_id;
  }

  if (event === 'journey_complete' || event === 'journey_abandon') {
    terminalJourneyIds.add(payload.journey_id);
    startedAtMap.delete(payload.journey_id);
  }

  emitter(event, withGovernance(payload));
  return payload.journey_id;
};

export const startJourney = (
  journeyName: string,
  firstStepName: string,
  options: Omit<JourneyStepInput, 'journey_name' | 'step_name' | 'step_order' | 'step_status'> = {},
  emitter: JourneyEmitter
): string => {
  const journeyId = options.journey_id || createJourneyId();
  setJourneyId(journeyName, journeyId);
  startedAtMap.set(journeyId, Date.now());

  emit(
    'journey_start',
    {
      journey_id: journeyId,
      journey_name: journeyName,
      step_name: firstStepName,
      step_order: 1,
      step_status: 'started',
      ...options,
    },
    emitter
  );

  return journeyId;
};

export const trackJourneyStep = (
  event: JourneyLifecycleEvent,
  input: JourneyStepInput,
  emitter: JourneyEmitter
): string | undefined => emit(event, input, emitter);

export const markJourneyFailure = (
  event: JourneyLifecycleEvent,
  input: Omit<JourneyStepInput, 'step_status'> & { error: unknown; operation: string },
  emitter: JourneyEmitter
): string | undefined => {
  const mappedFailure = mapApiErrorToTelemetry(input.operation, input.error);
  return emit(
    event,
    {
      ...input,
      step_status: event === 'journey_abandon' ? 'abandoned' : 'failed',
      operation: input.operation,
      http_status: mappedFailure.http_status as number | undefined,
      error_code: mappedFailure.error_code as string | undefined,
      retryable: mappedFailure.retryable as boolean | undefined,
    },
    emitter
  );
};

export const markFirstMilestone = (journeyName: string, milestone: string): boolean => {
  const storageKey = `rum_milestone:${journeyName}:${milestone}`;
  try {
    if (localStorage.getItem(storageKey)) {
      return false;
    }
    localStorage.setItem(storageKey, '1');
    return true;
  } catch {
    return false;
  }
};
